/**
 * High level router.
 *
 * Note: It's recommended to compose related routes in internal router
 * components (e.g: `core/pages/auth/AuthPage`, `core/pages/home/HomePage`).
 */

import React, { useEffect } from 'react';
import { Redirect, Route, Switch, withRouter, useLocation, useHistory } from 'react-router-dom';
import { shallowEqual, useSelector } from 'react-redux';
import HomePage from '../pages/home/HomePage';
import LogoutPage from '../pages/auth/Logout';
import ErrorsPage from '../pages/errors/ErrorsPage';
import AuthPage from '../pages/auth/AuthPage';
import FLOW_TYPES from '@core/constants/auth/flowTypes';
import { ROUTES } from '@core/constants/routes/routes';
import { STORAGE_KEYS } from '@core/constants/storage';
import { AUTH_STATUS } from '@core/constants/auth/status';

export const Routes = withRouter(() => {
    const { isAuthorized, userLastLocation, user } = useSelector(
        ({ auth }) => ({
            isAuthorized: auth.user != null && auth.user.status !== AUTH_STATUS.FAILURE,
            user: auth.user
        }),
        shallowEqual
    );
    const location = useLocation();
    const history = useHistory();
    // Password recovery/change lands on the OTP screen. If the user clicks the
    // email link while still logged in (e.g. changing their own password from
    // the profile), we must log them out first, otherwise Routes redirects any
    // `/auth/*` path to `/clients`.
    const otpParams = new URLSearchParams(location.search);
    const needsLogoutFirst =
        location.pathname.startsWith(ROUTES.AUTH.OTP) &&
        (otpParams.get('flowType') || otpParams.get('flowtype')) === FLOW_TYPES.PASSWORD;

    useEffect(() => {
        const isCorruptedUser = user && user.status === AUTH_STATUS.FAILURE;

        if (isCorruptedUser) {
            localStorage.removeItem(STORAGE_KEYS.SESSION_USER);
            localStorage.removeItem(STORAGE_KEYS.NEW_EMAIL);
            history.push('/logout');
            return;
        }

        if (!needsLogoutFirst || !isAuthorized) return;
        localStorage.removeItem(STORAGE_KEYS.SESSION_USER);
        localStorage.removeItem(STORAGE_KEYS.NEW_EMAIL);
        // Store the full destination (path + search) so Login can restore it
        // after the forced logout.
        localStorage.setItem(STORAGE_KEYS.RECOVER_TOKEN, location.pathname + location.search);
        history.push('/logout');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [needsLogoutFirst, isAuthorized, user]);

    // While the forced logout is in flight, render nothing so the authorized
    // `/auth -> /clients` redirect below doesn't race the logout.
    if (needsLogoutFirst && isAuthorized) return null;

    return (

        <Switch>
            {!isAuthorized ? (
            /* Render auth page when user at `/auth` and not authorized. */
                <AuthPage />
            ) : (
            /* Otherwise redirect to root page (`/`) */
                <Redirect from={ROUTES.AUTH.BASE} to={ROUTES.CLIENTS.BASE} />
            )}

            <Route path="/error" component={ErrorsPage} />
            <Route path="/logout" component={LogoutPage} />

            {!isAuthorized ? (
            /* Redirect to `/auth` when user is not authorized */
                <Redirect to={ROUTES.AUTH.LOGIN} />
            ) : (

                <HomePage userLastLocation={userLastLocation} />

            )}
        </Switch>
    );
});
